/**
 * RunSummary — score cards + match factors for the currently-selected
 * run on the Submission Detail page.
 *
 * The score cards are only meaningful for score-bearing prompt types (those
 * whose output schema is ObjectiveCorrectnessV1 / FreeformRubricV1). Extraction
 * types (extract_steps, extract_visuals, …) carry no Feedback, so the cards
 * would render all-N/A — hide them. Match factors still show when present.
 */
import { ScoreCards, type ScoreCardItem } from '@shared-ui/components';
import { resolveOutputSchemaType } from '@shared-ui/parsed-feedback';
import { SCORING_LABELS } from '@shared-ui/scoring-labels';
import type { ModelOutput } from '../../types';

interface Props {
    run: ModelOutput;
    promptType?: string;
}

export function RunSummary({ run, promptType }: Props) {
    const schema = resolveOutputSchemaType(promptType);
    const isScoreBearing = schema === 'ObjectiveCorrectnessV1' || schema === 'FreeformRubricV1';

    const feedback = run.parsedAssessment;
    const items: ScoreCardItem[] = [
        { label: 'Overall Score', value: feedback?.score },
        { label: SCORING_LABELS.correctness, value: feedback?.computedScores?.correctnessScore, comment: feedback?.computedScores?.correctnessScoreComment },
        { label: SCORING_LABELS.approach,    value: feedback?.computedScores?.approachScore,    comment: feedback?.computedScores?.approachScoreComment },
        { label: SCORING_LABELS.speed,       value: feedback?.computedScores?.speedScore,       comment: feedback?.computedScores?.speedScoreComment },
    ];

    return (
        <>
            {isScoreBearing && <ScoreCards items={items} className="prompt-score-cards" />}
            {run.matchScoreFactors && run.matchScoreFactors.length > 0 && (
                <div className="match-factors-row">
                    <label>Match Factors</label>
                    <div className="match-factors">
                        {run.matchScoreFactors.map((factor, idx) => (
                            <span key={idx} className="match-factor">{factor}</span>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
}
